import React, { useRef, useImperativeHandle } from "react";
import { useMembershipContext } from "@icoach/documents/membership/MembershipContext";
import { CheckboxField, Stack, Box } from "@curves/ui-base";
import { SignBox } from "@icoach/components";
import { NoticeCard, PrivacyNoticeCard } from "@icoach/documents/components";
import { getCheckboxValue, setCheckboxValue } from "@icoach/documents/tools";
import { refIsRequiredError, isEmpty } from "@util/utils";
import { resolveVersion } from "@icoach/documents/membership/create/versions";

const MembershipCreateContentSurvey = React.forwardRef((props, ref) => {
	const { data, errorModule, isReadOnly, getErrorModuleStatus } = useMembershipContext();
	const {
		contractVersion,
		contractMain = {}
	} = data;
	const { items = [], isConfirm, signImg } = data?.contractSurvey || {};

	const noticeRef = useRef([]);
	const isConfirmRef = useRef(null);
	const signImgRef = useRef(null);

	// 依 contractVersion 取得對應版本模組，再取出該版本的須知清單。
	// 找不到版本時 resolveVersion 會拋錯（見 versions/index.js），維持與現況相同的
	// 「未知版本本來就無法正常運作」行為定位，只是錯誤訊息更清楚。
	const versionModule = resolveVersion(contractVersion);
	const noticeList = versionModule.getNotice(contractMain?.membershipType, contractMain?.memberType);
	// 不寫 if (contractVersion >= 106)：「哪一版有什麼須知」的知識集中在 versions/，版本號不外流到呈現層。
	// 101-105 沒有 getPrivacyNotice，這裡拿到 undefined，個資卡片與其驗證整組不存在。
	const privacyNotice = versionModule.getPrivacyNotice?.();
	const privacyItemIndex = versionModule.PRIVACY_ITEM_INDEX;

	const getResult = () => {
		let result = {};
		const noticeObj = noticeRef.current;

		if (!isEmpty(noticeObj)) {
			result.items = noticeObj.map(item => item?.getResult?.()?.checked);
		}

		if (isConfirmRef.current) {
			result.isConfirm = getCheckboxValue(isConfirmRef.current?.getResult?.(), {
				type: "checked",
				isMultiple: false
			});
		}

		if (signImgRef.current) {
			result.signImg = signImgRef.current?.getResult?.();
		}

		return { contractSurvey: result };
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => {
				const allObj = {
					privacy: privacyNotice ? noticeRef.current[privacyItemIndex] : null,
					isConfirm: isConfirmRef.current,
					signImg: signImgRef.current
				};
				return refIsRequiredError.apply(null, Object.values(allObj));
			}
		}),
		// eslint-disable-next-line
		[]
	);
	return (
		<React.Fragment>
			<Box className="survey">
				{privacyNotice && (
					<PrivacyNoticeCard
						ref={el => (noticeRef.current[privacyItemIndex] = el)}
						notice={privacyNotice}
						img={privacyNotice.icon}
						defaultValue={items[privacyItemIndex]}
						readOnly={isReadOnly}
						required
					/>
				)}
				{
					noticeList.map((item, index) => (
						<NoticeCard key={index}
									ref={el => (noticeRef.current[index] = el)}
									defaultValue={items[index]}
									title={item?.title}
									img={item?.icon}
									noticeData={item?.details}
									WarnComponent={item?.WarnComponent}
									readOnly={isReadOnly} />
					))
				}
				<Stack direction={"column"} justifyContent={"center"} alignItems={"center"}>
					<CheckboxField
						key={setCheckboxValue(isConfirm)}
						ref={isConfirmRef}
						defaultChecked={setCheckboxValue(isConfirm)}
						{...getErrorModuleStatus(errorModule.contractSurvey, "isConfirm")}
						readOnly={isReadOnly}
						required
					>
						<CheckboxField.Checkbox label="本人已了解並詳讀以上會員須知，並同意遵守。" />
					</CheckboxField>
					<SignBox ref={signImgRef} title="會員簽名"
							 defaultValue={signImg} {...getErrorModuleStatus(errorModule.contractSurvey, "signImg")}
							 readOnly={isReadOnly} />
				</Stack>
			</Box>
		</React.Fragment>
	);
});

export default MembershipCreateContentSurvey;
